import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Heart, Eye, MapPin, Shield, Users, Home, Scale } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
interface Property {
  id: string;
  title: string;
  price_egp: number;
  deposit_egp: number;
  distance_km: number;
  unit_type: string;
  furnished: boolean;
  verified: boolean;
  photos: string[];
  amenities: string[];
  available_from: string;
  lease_min_months: number;
  gender_rules: string;
  universities?: {
    name_en: string;
    name_ar: string;
  };
}
interface ListingCardProps {
  currentLang: string;
  onViewClick?: (listing: any) => void;
  onAddToComparison?: (property: Property) => void;
  listing: {
    id: string;
    title: string;
    titleAr: string;
    price: number;
    deposit: number;
    university: string;
    distance: number;
    furnished: boolean;
    verified: boolean;
    images: string[];
    unitType: 'room' | 'shared' | 'entire';
    genderRules: 'mixed' | 'female' | 'male';
    responseRate: number;
    ownerName: string;
  };
}
const ListingCard = ({
  currentLang,
  listing,
  onViewClick,
  onAddToComparison
}: ListingCardProps) => {
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const navigate = useNavigate();

  // Check if item is already in wishlist when component mounts
  useEffect(() => {
    const checkWishlistStatus = async () => {
      try {
        const {
          data: {
            user
          }
        } = await supabase.auth.getUser();
        if (!user) return;
        const {
          data,
          error
        } = await supabase.from('wishlists').select('id').eq('user_id', user.id).eq('listing_id', listing.id).single();
        if (error && error.code !== 'PGRST116') {
          console.error('Error checking wishlist status:', error);
          return;
        }
        setIsWishlisted(!!data);
      } catch (error) {
        console.error('Error checking wishlist status:', error);
      }
    };
    checkWishlistStatus();
  }, [listing.id]);
  const getPriceClass = (price: number) => {
    if (price <= 1500) return 'price-budget';
    if (price <= 4000) return 'price-mid';
    return 'price-premium';
  };
  const getDistanceClass = (distance: number) => {
    if (distance <= 1) return 'distance-close';
    if (distance <= 3) return 'distance-medium';
    return 'distance-far';
  };
  const getUnitTypeLabel = (type: string) => {
    const labels = {
      room: {
        en: 'Private Room',
        ar: 'غرفة خاصة'
      },
      shared: {
        en: 'Shared Room',
        ar: 'غرفة مشتركة'
      },
      entire: {
        en: 'Entire Unit',
        ar: 'وحدة كاملة'
      }
    };
    return currentLang === 'ar' ? labels[type as keyof typeof labels]?.ar : labels[type as keyof typeof labels]?.en;
  };
  const getGenderLabel = (gender: string) => {
    const labels = {
      mixed: {
        en: 'Mixed',
        ar: 'مختلط'
      },
      female: {
        en: 'Female Only',
        ar: 'إناث فقط'
      },
      male: {
        en: 'Male Only',
        ar: 'ذكور فقط'
      }
    };
    return currentLang === 'ar' ? labels[gender as keyof typeof labels]?.ar : labels[gender as keyof typeof labels]?.en;
  };
  const handleCardClick = async () => {
    // Check if user is authenticated
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // User is not signed in, show notification and redirect
        toast.error(
          currentLang === 'ar' 
            ? 'يجب تسجيل الدخول أولاً لعرض تفاصيل العقار' 
            : 'You must sign in first to view property details'
        );
        navigate('/signin');
        return;
      }
      
      // User is authenticated, proceed with viewing
      if (onViewClick) {
        onViewClick(listing);
      }
    } catch (error) {
      console.error('Error checking authentication:', error);
      toast.error(currentLang === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
  };
  const handleWishlistClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setWishlistLoading(true);
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) {
        toast.error(currentLang === 'ar' ? 'يجب تسجيل الدخول أولاً' : 'Please sign in first');
        return;
      }
      if (isWishlisted) {
        const {
          error
        } = await supabase.from('wishlists').delete().eq('user_id', user.id).eq('listing_id', listing.id);
        if (error) {
          console.error('Error removing from wishlist:', error);
          toast.error(currentLang === 'ar' ? 'حدث خطأ' : 'An error occurred');
          return;
        }
        setIsWishlisted(false);
        toast.success(currentLang === 'ar' ? 'تم إزالة العقار من المفضلة' : 'Removed from wishlist');
      } else {
        const {
          error
        } = await supabase.from('wishlists').insert({
          user_id: user.id,
          listing_id: listing.id
        });
        if (error) {
          console.error('Error adding to wishlist:', error);
          toast.error(currentLang === 'ar' ? 'حدث خطأ' : 'An error occurred');
          return;
        }
        setIsWishlisted(true);
        toast.success(currentLang === 'ar' ? 'تم إضافة العقار للمفضلة' : 'Added to wishlist');
      }
    } catch (error) {
      console.error('Error toggling wishlist:', error);
      toast.error(currentLang === 'ar' ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setWishlistLoading(false);
    }
  };
  const handleViewClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Check if user is authenticated
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // User is not signed in, show notification and redirect
        toast.error(
          currentLang === 'ar' 
            ? 'يجب تسجيل الدخول أولاً لعرض تفاصيل العقار' 
            : 'You must sign in first to view property details'
        );
        navigate('/signin');
        return;
      }
      
      // User is authenticated, proceed with viewing
      if (onViewClick) {
        onViewClick(listing);
      }
    } catch (error) {
      console.error('Error checking authentication:', error);
      toast.error(currentLang === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
  };
  const handleAddToComparison = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Check if user is authenticated
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        // User is not signed in, show notification and redirect
        toast.error(
          currentLang === 'ar' 
            ? 'يجب تسجيل الدخول أولاً لإضافة العقار للمقارنة' 
            : 'You must sign in first to add properties to comparison'
        );
        navigate('/signin');
        return;
      }
      
      // User is authenticated, proceed with adding to comparison
      if (onAddToComparison) {
        const propertyForComparison: Property = {
          id: listing.id,
          title: listing.title,
          price_egp: listing.price,
          deposit_egp: listing.deposit,
          distance_km: listing.distance,
          unit_type: listing.unitType,
          furnished: listing.furnished,
          verified: listing.verified,
          photos: listing.images,
          amenities: [],
          available_from: new Date().toISOString(),
          lease_min_months: 12,
          gender_rules: listing.genderRules,
          universities: {
            name_en: listing.university,
            name_ar: listing.university
          }
        };
        onAddToComparison(propertyForComparison);
        toast.success(currentLang === 'ar' ? 'تم إضافة العقار للمقارنة' : 'Property added to comparison');
      }
    } catch (error) {
      console.error('Error checking authentication for comparison:', error);
      toast.error(currentLang === 'ar' ? 'حدث خطأ' : 'An error occurred');
    }
  };
  return <Card className="group hover:shadow-lg transition-all duration-300 border-0 bg-gradient-to-br from-card to-card/80 animate-fade-in cursor-pointer" onClick={handleCardClick}>
      <CardContent className="p-0">
        {/* Image */}
        <div className="relative h-48 overflow-hidden rounded-t-lg">
          <img src={listing.images[0]} alt={currentLang === 'ar' ? listing.titleAr : listing.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          
          {/* Badges Overlay */}
          <div className="absolute top-3 left-3 flex flex-col gap-2">
            {listing.verified && <Badge className="verified trust-badge">
                <Shield className="h-3 w-3 mr-1" />
                {currentLang === 'ar' ? 'مُتحقق' : 'Verified'}
              </Badge>}
            {listing.furnished && <Badge variant="secondary" className="trust-badge">
                <Home className="h-3 w-3 mr-1" />
                {currentLang === 'ar' ? 'مفروش' : 'Furnished'}
              </Badge>}
          </div>

          {/* Heart Icon */}
          <Button variant="ghost" size="icon" className="absolute top-3 right-3 h-8 w-8 bg-background/80 hover:bg-background" onClick={handleWishlistClick} disabled={wishlistLoading}>
            <Heart className={`h-4 w-4 ${isWishlisted ? 'fill-red-500 text-red-500' : ''}`} />
          </Button>

          {/* Price Badge */}
          <div className="absolute bottom-3 left-3">
            <div className="bg-background/95 backdrop-blur rounded-lg px-3 py-1">
              <span className={`text-lg font-bold ${getPriceClass(listing.price)}`}>
                {listing.price.toLocaleString()} EGP
              </span>
              <span className="text-xs text-muted-foreground ml-1">
                {currentLang === 'ar' ? '/شهر' : '/month'}
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Title */}
          <h3 className="font-semibold text-lg mb-2 line-clamp-1">
            {currentLang === 'ar' ? listing.titleAr : listing.title}
          </h3>

          {/* Location & Distance */}
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{listing.university}</span>
            <span className={`text-sm font-medium ${getDistanceClass(listing.distance)}`}>
              • {listing.distance}km {currentLang === 'ar' ? 'من الجامعة' : 'from campus'}
            </span>
          </div>

          {/* Properties */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Badge variant="outline" className="text-xs">
              {getUnitTypeLabel(listing.unitType)}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {getGenderLabel(listing.genderRules)}
            </Badge>
            <Badge variant="outline" className="text-xs">
              <Users className="h-3 w-3 mr-1" />
              {listing.responseRate}% {currentLang === 'ar' ? 'معدل الرد' : 'response'}
            </Badge>
          </div>

          {/* Owner Info & Actions */}
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="text-sm text-muted-foreground truncate flex-1 min-w-0">
              {currentLang === 'ar' ? 'المالك:' : 'Owner:'} {listing.ownerName}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              {onAddToComparison && <Button size="sm" variant="outline" onClick={handleAddToComparison} className="flex items-center gap-1" title={currentLang === 'ar' ? 'إضافة للمقارنة' : 'Add to Compare'}>
                  <Scale className="h-4 w-4" />
                  
                </Button>}
              <Button size="sm" className="bg-primary hover:bg-primary-hover" onClick={handleViewClick}>
                <Eye className="h-4 w-4 mr-2" />
                {currentLang === 'ar' ? 'عرض' : 'View'}
              </Button>
            </div>
          </div>

          {/* Deposit Info */}
          {listing.deposit > 0 && <div className="mt-2 pt-2 border-t text-xs text-muted-foreground">
              {currentLang === 'ar' ? 'تأمين:' : 'Deposit:'} {listing.deposit.toLocaleString()} EGP
            </div>}
        </div>
      </CardContent>
    </Card>;
};
export default ListingCard;